import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Avatar,
  Typography,
  Tabs,
  Tab,
  Box,
  Grid,
  Tooltip,
  IconButton,
} from "@mui/material";
import styles from "./Profile.module.css";
import Dropdown1 from "../Dropdown/Dropdown1";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Privacy from "./Privacy/Privacy";
import Visibility from "@mui/icons-material/Visibility";
import {
  useLoaderContext,
  useToastContext,
} from "../../../Context/ToastContext";
import ViewDocument from "../ViewDocument/ViewDocument";
import { useUserContext } from "../../../Context/UserContext";
import { ClearIcon } from "@mui/x-date-pickers";
import { Helmet } from "react-helmet";
import { apiGet, apiPut } from "../../../utils/ApiManager";

const fetchProfileBasicData = process.env.REACT_APP_FETCH_PROFILE_DATA;
const fetchGenders = process.env.REACT_APP_FETCH_GENDER_OPTIONS;
const fetchDropdownCountries = process.env.REACT_APP_FETCH_DROPDOWN_COUNTRIES;
const fetchDropdownStates = process.env.REACT_APP_FETCH_DROPDOWN_STATES;
const fetchDropdownCities = process.env.REACT_APP_FETCH_DROPDOWN_CITIES;
const fetchProfileImage = process.env.REACT_APP_FETCH_PROFILE_IMAGE;

const Profile = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    gender: "",
    aadhaarNo: "",
    aadhaarCard: null,
    aadhaarFile: null,
    panNo: "",
    panCard: null,
    panFile: null,
    phoneNo: "",
    email: "",
    addressLine: "",
    pincode: "",
    city: "",
    state: "",
    country: "India",
  });
  const { showToast } = useToastContext();
  const { setLoading } = useLoaderContext();
  const [profileData, setProfileData] = useState({});
  const [genderOptions, setGenderOptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [aadhaarCard, setAadhaarCard] = useState(null);
  const [panCard, setPanCard] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [showDocument, setShowDocument] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [error, setError] = useState("");
  const { requestConfirmation } = useUserContext();
  const panFileInputRef = useRef(null);
  const aadhaarFileInputRef = useRef(null);

  // Function to fetch profile data
  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await apiGet(fetchProfileBasicData);
      if (response.success) {
        const profileData = response.data;
        const formattedData = {
          firstName: profileData.firstName || "",
          lastName: profileData.lastName || "",
          dob: profileData.dob || "",
          gender: profileData.gender || "",
          aadhaarNo: profileData.aadhaarNo || "",
          panNo: profileData.panNo || "",
          phoneNo: profileData.phoneNo || "",
          email: profileData.email || "",
          addressLine: profileData.address?.addressLine || "",
          pincode: profileData.address?.pincode || "",
          city: profileData.address?.city || "",
          state: profileData.address?.state || "",
          country: profileData.address?.country || "India",
          image: profileData.image || "",
          panCard: null,
          aadhaarCard: null,
          panFile: profileData.panCard || "",
          aadhaarFile: profileData.aadhaarCard || "",
        };
        setFormData({ ...formattedData });
        setProfileData({ ...formattedData });
      } else {
        showToast(response.message || "Failed to fetch profile data.", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Session expired, please log in again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (documentUrl) => {
    setDocumentUrl(documentUrl);
    setShowDocument(true);
  };

  const handleCloseDocument = () => {
    setDocumentUrl("");
    setShowDocument(false);
  };

  // Function to fetch other data like gender, countries, etc.
  const fetchData = async () => {
    try {
      setLoading(true);
      await fetchProfileData(); // Fetch profile data first
      await fetchGenderOptions(); // Fetch gender options
      await fetchCountries(); // Fetch countries
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(); // on component mounting thiswill load and display the data
  }, []); // Empty dependency array to run once when component mounts

  const fetchGenderOptions = async () => {
    try {
      // setLoading(true);
      const response = await apiGet(fetchGenders);
      if (response?.success) {
        setGenderOptions(response.data || []);
      } else {
        showToast(
          response.message || "Failed to fetch gender options.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error?.response.message ||
          error?.message ||
          "Failed to fetch gender options.",
        "error"
      );
    } finally {
      // setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await apiGet(fetchDropdownCountries);
      if (response?.success) {
        setCountries(response?.data || []);
      } else {
        showToast(response?.message || "Failed to fetch countries.", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Failed to fetch countries.",
        "error"
      );
    }
  };

  const fetchStates = async () => {
    try {
      setLoading(true);
      const response = await apiGet(
        `${fetchDropdownStates}=${formData.country}`
      );
      if (response?.success) {
        setStates(response.data || []);
      } else {
        showToast(response.message || "Failed to fetch states.", "error");
      }
    } catch (error) {
      showToast(
        error?.response.message || error?.message || "Failed to fetch states.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      setLoading(true);

      const response = await apiGet(
        `${fetchDropdownCities}=${formData.state}&country=${formData.country}`
      );

      if (response?.success) {
        setCities(response.data || []);
      } else {
        showToast(response.message || "Failed to fetch cities.", "error");
      }
    } catch (error) {
      showToast(
        error?.response.message || error?.message || "Failed to fetch cities.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];

    if (file) {
      // Validate file size (300 KB = 307200 bytes)
      const maxSizeInBytes = 300 * 1024;
      if (file.size > maxSizeInBytes) {
        setError("Image must be less than 300 KB.");
        return;
      }

      const formData = new FormData();
      formData.append("image", file);

      try {
        setError("");
        setLoading(true);
        const response = await apiPut(fetchProfileImage, formData, true);

        if (response?.image) {
          localStorage.setItem("profileImage", response.image);
          setProfileImage(response.image);
          showToast("Profile Image updated successfully.", "success");
        } else {
          showToast(
            response.message || "Failed to update profile image.",
            "error"
          );
        }
        // Handle success response here...
      } catch (error) {
        showToast(
          error?.response.message ||
            error?.message ||
            "Failed to update profile image.",
          "error"
        );
        setError("Something went wrong while uploading.");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setProfileImage(localStorage.getItem("profileImage"));
  }, []);

  const handleUpdate = async () => {
    const result = await requestConfirmation("Want to save these details ?");
    if (!result) return;
    const data = new FormData();

    // Append form data
    Object.keys(formData).forEach((key) => {
      data.append(key, formData[key]);
    });

    // Append Aadhaar and PAN card files if they are present
    if (aadhaarCard) {
      data.append("aadhaarCard", aadhaarCard);
    }
    if (panCard) {
      data.append("panCard", panCard);
    }

    try {
      setLoading(true);

      const response = await apiPut(fetchProfileBasicData, data, true);

      if (response.success) {
        setProfileData(formData);
        showToast(
          response.message || "Profile updated successfully !",
          "success"
        );
      } else {
        showToast(response.message || "Failed to update profile !", "error");
      }
    } catch (error) {
      showToast(
        error?.response.message ||
          error?.message ||
          "Failed to update profile !",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // First Name
    if (!formData.firstName) {
      newErrors.firstName = "First name is required";
      isValid = false;
    }

    // Last Name
    if (!formData.lastName) {
      newErrors.lastName = "Last name is required";
      isValid = false;
    }

    // DOB
    if (!formData.dob) {
      newErrors.dob = "Date of birth is required";
      isValid = false;
    }

    //Gender
    if (!formData.gender) {
      newErrors.gender = "Gender is required";
      isValid = false;
    }

    //Aadhaar
    const aadhaarRegex = /^[0-9]{12}$/;
    if (!formData.aadhaarNo) {
      newErrors.aadhaarNo = "Aadhaar number is required";
      isValid = false;
    } else if (!aadhaarRegex.test(formData.aadhaarNo)) {
      newErrors.aadhaarNo = "Aadhar number must be exactly 12 digits";
      isValid = false;
    }

    //PAN
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!formData.panNo) {
      newErrors.panNo = "PAN is required";
      isValid = false;
    } else if (formData.panNo.length !== 10 || !panRegex.test(formData.panNo)) {
      newErrors.panNo = "Valid format of 10 characters (uppercase)";
      isValid = false;
    }

    // Phone Number
    const phoneRegex = /^[0-9]{10}$/;
    if (!formData.phoneNo) {
      newErrors.phoneNo = "Phone number is required";
      isValid = false;
    } else if (!phoneRegex.test(formData.phoneNo)) {
      newErrors.phoneNo = "Phone number must be exactly 10 digits";
      isValid = false;
    }

    // Email
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Address
    if (!formData.addressLine) {
      newErrors.addressLine = "Permanent address is required";
      isValid = false;
    }

    // Pincode
    if (!formData.pincode) {
      newErrors.pincode = "Pincode is required";
    } else if (formData.pincode < 110000 || formData.pincode > 855999) {
      newErrors.pincode = "Pincode must be between 110000 and 855999";
      isValid = false;
    }

    // City
    if (!formData.city) {
      newErrors.city = "Please select a city";
      isValid = false;
    }

    // State
    if (!formData.state) {
      newErrors.state = "Please select a state";
      isValid = false;
    }

    // Country
    if (!formData.country) {
      newErrors.country = "Please select a country";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    const isValid = validateForm();
    setIsButtonDisabled(!isValid);
  }, [formData]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (formData.country) {
      fetchStates();
    }
  }, [formData.country]);

  useEffect(() => {
    if (formData.state) {
      fetchCities();
    }
  }, [formData.state]);

  const handleDateChange = (newValue) => {
    if (newValue) {
      const dob = dayjs(newValue);

      // Check if the date is valid
      if (!dob.isValid()) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          dob: "Invalid date format",
        }));
      }
      // Check if the date is in the future
      else if (dob.isAfter(dayjs(), "day")) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          dob: "Date of Birth cannot be in the future",
        }));
      }
      // If valid, update the state and clear errors
      else {
        setFormData({
          ...formData,
          dob: dob.toISOString(), // Store the date as an ISO string
        });
        setErrors((prevErrors) => ({
          ...prevErrors,
          dob: "", // Clear any existing errors
        }));
      }
    } else {
      // Handle case when newValue is empty (e.g., user clears the input)
      setErrors((prevErrors) => ({
        ...prevErrors,
        dob: "This field is required",
      }));
      setFormData({
        ...formData,
        dob: "", // Reset the value if input is cleared
      });
    }
  };

  const handleClick = (inputRef) => {
    if (inputRef?.current) {
      inputRef.current.value = null;
    }
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const result = await requestConfirmation(
        "Do you want to update your profile image?"
      );
      if (result) {
        handleImageUpload({ target: { files: [file] } });
      }
    }
  };

  const handleDocumentUploadChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 300 * 1024) {
      setErrors((prev) => ({
        ...prev,
        [fieldName === "aadhaarCard" ? "aadhaarDocument" : "panDocument"]:
          "File size must be less than 300KB",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    const fileURL = URL.createObjectURL(file);

    if (fieldName === "aadhaarCard") {
      setFormData((prev) => ({ ...prev, aadhaarFile: fileURL }));
    } else if (fieldName === "panCard") {
      setFormData((prev) => ({ ...prev, panFile: fileURL }));
    }

    // Clear any previous error
    setErrors((prev) => ({
      ...prev,
      [fieldName === "aadhaarCard" ? "aadhaarDocument" : "panDocument"]: "",
    }));
  };

  const clearFile = (field) => {
    const previewField = field.replace("Document", "Preview");
    const fileField = field.replace("Document", "File");

    if (formData[previewField]) {
      URL.revokeObjectURL(formData[previewField]); // cleanup blob URL
    }

    setFormData((prev) => ({
      ...prev,
      [field]: null,
      [previewField]: null,
      [fileField]: null,
    }));
  };

  return (
    <Box className={styles.mainProfilePage}>
      <Helmet>
        <title>Profile | DiNEST</title>
        <meta name="description" content="View profile on DiNEST" />
      </Helmet>
      <Box className={styles.mainContainer}>
        {/* Profile Card */}
        <Card className={styles.cardProfileImage}>
          <CardContent>
            <Box>
              <Tooltip title="Click to update profile picture" arrow>
                <Box
                  onClick={() =>
                    document.getElementById("avatar-upload").click()
                  }
                  sx={{
                    cursor: "pointer",
                    "&:hover .overlay": { opacity: 1 },
                  }}
                >
                  <Avatar
                    src={
                      profileImage
                        ? `data:image/png;base64,${profileImage}`
                        : "/default-avatar.jpg"
                    }
                    alt="Profile Image"
                    sx={{
                      width: 180,
                      height: 180,
                      margin: "0 auto",
                      mb: 3,
                      cursor: "pointer",
                    }}
                  />
                </Box>
              </Tooltip>

              {/* Hidden file input */}
              <input
                type="file"
                accept="image/*"
                id="avatar-upload"
                style={{ display: "none" }}
                onChange={handleProfileImageChange}
              />
            </Box>
            {error && (
              <p
                className={styles.error}
                style={{ minHeight: "1.5rem", textAlign: "center" }}
              >
                {error}
              </p>
            )}

            <Typography variant="h5" textAlign="center">
              {profileData.firstName || profileData.lastName
                ? `${profileData.firstName || "N/A"} ${
                    profileData.lastName || "N/A"
                  }`.trim()
                : "Your Name"}
            </Typography>
          </CardContent>
        </Card>

        {/* Tabs Section */}
        <Card className={styles.tabContainer}>
          <CardContent>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              sx={{ mb: 2 }}
              className={styles.tabs}
            >
              <Tab label="My Profile" />
              {/* <Tab label="Company Profile" /> */}
              <Tab label="Privacy" />
            </Tabs>

            {/* Conditional Rendering for Forms */}
            {selectedTab === 0 && (
              <Box className={styles.inputBox} component="form">
                <Grid container spacing={2}>
                  <Grid className={styles.gridInput}>
                    <TextField
                      label="First Name"
                      fullWidth
                      type="text"
                      variant="outlined"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className={styles.TextFieldInput}
                      sx={{ backgroundColor: " #e8f2ec" }}
                    />
                    {errors.firstName && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.firstName}
                      </p>
                    )}
                  </Grid>

                  <Grid className={styles.gridInput}>
                    <TextField
                      label="Last Name"
                      type="text"
                      fullWidth
                      variant="outlined"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className={styles.TextFieldInput}
                      sx={{ backgroundColor: " #e8f2ec" }}
                    />
                    {errors.lastName && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.lastName}
                      </p>
                    )}
                  </Grid>

                  <Grid sx={{ width: 200 }} className={styles.gridInput}>
                    <Dropdown1
                      label="Gender"
                      variant="outlined"
                      fullWidth
                      name="gender"
                      className={styles.inputGender}
                      value={formData.gender}
                      onChange={handleInputChange}
                      options={genderOptions}
                      sx={{ backgroundColor: " #e8f2ec" }}
                    />
                    {errors.gender && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.gender}
                      </p>
                    )}
                  </Grid>

                  <Grid className={styles.gridInput}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        label="Date of Birth"
                        fullWidth
                        className={styles.datePicker}
                        views={["year", "month", "day"]}
                        format="DD/MM/YYYY"
                        inputFormat="DD/MM/YYYY"
                        value={formData.dob ? dayjs(formData.dob) : null}
                        onChange={handleDateChange}
                        InputLabelProps={{ shrink: true }}
                        sx={{ backgroundColor: " #e8f2ec" }}
                      />
                    </LocalizationProvider>
                    {errors.dob && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.dob}
                      </p>
                    )}
                  </Grid>

                  <Grid className={styles.gridInput}>
                    <TextField
                      label="Phone Number"
                      fullWidth
                      type="number"
                      variant="outlined"
                      name="phoneNo"
                      value={formData.phoneNo}
                      onChange={handleInputChange}
                      className={styles.inputPhoneNumber}
                      sx={{ backgroundColor: " #e8f2ec" }}
                      InputProps={{
                        inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                        classes: { input: styles.noArrows },
                      }}
                    />
                    {errors.phoneNo && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.phoneNo}
                      </p>
                    )}
                  </Grid>

                  <Grid className={styles.gridInput}>
                    <TextField
                      label="Aadhar Number"
                      type="number"
                      fullWidth
                      variant="outlined"
                      name="aadhaarNo"
                      value={formData.aadhaarNo}
                      onChange={handleInputChange}
                      className={styles.inputAadharNumber}
                      sx={{ backgroundColor: " #e8f2ec" }}
                      InputProps={{
                        inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                        classes: { input: styles.noArrows },
                      }}
                    />
                    {errors.aadhaarNo && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.aadhaarNo}
                      </p>
                    )}
                  </Grid>

                  <Grid className={styles.gridInput}>
                    <TextField
                      label="PAN"
                      type="text"
                      fullWidth
                      variant="outlined"
                      name="panNo"
                      value={formData.panNo}
                      onChange={handleInputChange}
                      className={styles.inputPanNumber}
                      sx={{ backgroundColor: " #e8f2ec" }}
                    />
                    {errors.panNo && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.panNo}
                      </p>
                    )}
                  </Grid>

                  <Grid className={styles.gridInput}>
                    <TextField
                      sx={{ backgroundColor: "#e8f2ec" }}
                      label="Email Address"
                      type="email"
                      variant="outlined"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={styles.inputEmailAddress}
                      fullWidth
                    />
                    {errors.email && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.email}
                      </p>
                    )}
                  </Grid>

                  <Grid className={styles.gridInput}>
                    <TextField
                      label="Pincode"
                      fullWidth
                      type="number"
                      variant="outlined"
                      name="pincode"
                      sx={{ backgroundColor: " #e8f2ec" }}
                      value={formData.pincode}
                      className={styles.inputAadharNumber}
                      onChange={handleInputChange}
                      InputProps={{
                        inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                        classes: { input: styles.noArrows },
                      }}
                    />
                    {errors.pincode && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.pincode}
                      </p>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12 }} className={styles.gridInput}>
                    <TextField
                      label="Permanent Address"
                      fullWidth
                      type="text"
                      variant="outlined"
                      name="addressLine"
                      sx={{ backgroundColor: " #e8f2ec" }}
                      value={formData.addressLine}
                      onChange={handleInputChange}
                      className={styles.inputPermanentAddress}
                    />
                    {errors.addressLine && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.addressLine}
                      </p>
                    )}
                  </Grid>

                  <Grid sx={{ width: 220 }} className={styles.gridInput}>
                    <Dropdown1
                      label="Country"
                      variant="outlined"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      options={countries}
                      className={styles.inputAadharNumber}
                    />
                    {errors.country && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.country}
                      </p>
                    )}
                  </Grid>

                  <Grid sx={{ width: 220 }} className={styles.gridInput}>
                    <Dropdown1
                      label="State"
                      variant="outlined"
                      name="state"
                      value={formData.state}
                      options={states}
                      onChange={handleInputChange}
                    />
                    {errors.state && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.state}
                      </p>
                    )}
                  </Grid>

                  <Grid sx={{ width: 220 }} className={styles.gridInput}>
                    <Dropdown1
                      label="City"
                      variant="outlined"
                      name="city"
                      value={formData.city}
                      options={cities}
                      onChange={handleInputChange}
                    />
                    {errors.city && (
                      <p
                        className={styles.error}
                        style={{ minHeight: "1.5rem" }}
                      >
                        {errors.city}
                      </p>
                    )}
                  </Grid>

                  {/* Aadhaar Card */}
                  <Grid
                    size={{ xs: 6 }}
                    md={4}
                    className={styles.cardGridContainer}
                  >
                    <Grid className={styles.uploadPanContainer}>
                      <Grid container direction="column">
                        <Typography
                          variant="body1"
                          className={styles.uploadImageTitle}
                        >
                          {" "}
                          Upload AADHAAR Document
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          (PDF &lt; 300kb)
                        </Typography>
                        {/* File Upload Button */}
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          className={styles.uploadButton}
                          onClick={handleClick}
                          startIcon={<CloudUploadIcon />}
                        >
                          Upload
                          <input
                            type="file"
                            hidden
                            name="aadhaarCard"
                            ref={aadhaarFileInputRef} // FILE REF
                            onChange={(e) =>
                              handleDocumentUploadChange(e, "aadhaarCard")
                            }
                            accept="application/pdf"
                          />
                        </Button>
                        {errors.aadhaarDocument && (
                          <p
                            className={styles.error}
                            style={{ minHeight: "1.5rem" }}
                          >
                            {errors.aadhaarDocument}
                          </p>
                        )}
                        {/* Selected File Name + Remove Icon */}
                        {formData.aadhaarCard && (
                          <Box display="flex" justifyContent="center">
                            <Box display="flex" alignItems="center">
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "inline-block",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                  maxWidth: 120,
                                  cursor: "pointer",
                                }}
                              >
                                {formData?.aadhaarCard?.name}
                              </Typography>
                              <IconButton
                                onClick={() => clearFile("aadhaarCard")}
                                size="small"
                                sx={{ ml: 0.5 }}
                              >
                                <ClearIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        )}

                        {/* Preview or Icon */}

                        <Box>
                          {formData.aadhaarFile && (
                            <IconButton
                              onClick={() =>
                                handleViewDocument(formData.aadhaarFile)
                              }
                              sx={{
                                ml: 1,
                                "&:hover": { backgroundColor: "transparent" },
                              }}
                            >
                              <Tooltip title="View Aadhaar Document" arrow>
                                <Visibility sx={{ fontSize: "2rem" }} />
                              </Tooltip>
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    </Grid>

                    {/* PAN Card */}
                    <Grid className={styles.uploadPanContainer}>
                      <Grid container direction="column">
                        <Typography
                          variant="body1"
                          className={styles.uploadImageTitle}
                        >
                          {" "}
                          Upload PAN Document
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          (PDF &lt; 300kb)
                        </Typography>
                        {/* File Upload Button */}
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          className={styles.uploadButton}
                          onClick={handleClick}
                          startIcon={<CloudUploadIcon />}
                        >
                          Upload
                          <input
                            type="file"
                            hidden
                            name="panCard"
                            ref={panFileInputRef} // FILE REF
                            onChange={(e) =>
                              handleDocumentUploadChange(e, "panCard")
                            }
                            accept="application/pdf"
                          />
                        </Button>
                        {errors.panDocument && (
                          <p className={styles.errorUpload}>
                            {errors.panDocument}
                          </p>
                        )}
                        {/* Selected File Name + Remove Icon */}
                        {formData.panCard && (
                          <Box display="flex" justifyContent="center">
                            <Box display="flex" alignItems="center">
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "inline-block",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                  maxWidth: 120,
                                  cursor: "pointer",
                                }}
                              >
                                {formData?.panCard?.name}
                              </Typography>
                              <IconButton
                                onClick={() => clearFile("panCard")}
                                size="small"
                                sx={{ ml: 0.5 }}
                              >
                                <ClearIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        )}

                        {/* Preview or Icon */}
                        <Box>
                          {formData.panFile && (
                            <IconButton
                              onClick={() =>
                                handleViewDocument(formData.panFile)
                              }
                              sx={{
                                ml: 1,
                                "&:hover": { backgroundColor: "transparent" },
                              }}
                            >
                              <Tooltip title="View PAN Document" arrow>
                                <Visibility sx={{ fontSize: "2rem" }} />
                              </Tooltip>
                            </IconButton>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Button
                  sx={{backgroundColor:'#207d46'}}
                    variant="contained"
                    onClick={handleUpdate}
                    disabled={isButtonDisabled}
                    className={styles.saveButton}
                  >
                    Save
                  </Button>
                </Grid>
              </Box>
            )}

            {selectedTab === 1 && <Privacy />}
          </CardContent>
        </Card>
      </Box>
      <ViewDocument
        documentUrl={documentUrl}
        isOpen={showDocument}
        onClose={handleCloseDocument}
      />
    </Box>
  );
};

export default Profile;
